import React, { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import startCyclingPoseDetection from "../utils/cycling/CyclingPoseDetector";
import {
  CYCLING_ANGLES,
  computeCyclingAngles,
  getAngleStatus,
  getCloserSide,
  getAngleLandmarkPositions,
  getCyclingAnglesForBikeType,
  BIKE_TYPES,
  getBikeTypeDisplayName,
} from "../utils/cycling/CyclingAngles";
import { CyclingAnalyzer } from "../utils/cycling/CyclingAnalyzer";
import { generateBikeFitTips } from "../utils/cycling/BikeFitRecommendations";
import { 
  enableDebugMode, 
  disableDebugMode, 
  validateMeasurementConsistency, 
  printValidationReport 
} from "../utils/cycling/DebugTools";

const SKELETON_CONNECTIONS = [
  [11, 13], [13, 15], [12, 14], [14, 16],
  [11, 12], [11, 23], [12, 24], [23, 24],
  [23, 25], [24, 26], [25, 27], [26, 28],
  [27, 29], [28, 30], [29, 31], [30, 32],
];

export default function CyclingPosturePage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const analyzerRef = useRef(new CyclingAnalyzer());

  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [liveAngles, setLiveAngles] = useState({});
  const [liveSide, setLiveSide] = useState(null);
  const [frameCount, setFrameCount] = useState(0);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [status, setStatus] = useState("idle");
  const [videoDimensions, setVideoDimensions] = useState({ w: 480, h: 360 });
  const [bikeType, setBikeType] = useState(BIKE_TYPES.RECREATIONAL);
  const [debugMode, setDebugMode] = useState(false);

  // ── Video upload ──────────────────────────────────────────────
  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    loadVideoFromUrl(URL.createObjectURL(file));
  };

  const loadVideoFromUrl = useCallback((url) => {
    setResults(null);
    setShowResults(false);
    analyzerRef.current.reset();
    setFrameCount(0);
    setStatus("loading");
    videoRef.current.src = url;
    videoRef.current.onloadeddata = () => {
      videoRef.current.pause();
      setVideoDimensions({ w: videoRef.current.videoWidth, h: videoRef.current.videoHeight });
      setVideoLoaded(true);
      setStatus("ready");
    };
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("video/")) {
      loadVideoFromUrl(URL.createObjectURL(file));
    }
  };

  // ── Pose detection callback ───────────────────────────────────
  const handlePoseResults = useCallback((landmarks, videoEnded) => {
    if (!landmarks) {
      setIsAnalyzing(false);
      if (videoEnded || analyzerRef.current.frameCount > 30) {
        const res = analyzerRef.current.computeResults();
        setResults(res);
        setShowResults(true);
        setStatus("done");
      } else {
        setStatus("ready");
      }
      return;
    }

    const side = getCloserSide(landmarks);
    const angles = computeCyclingAngles(landmarks, side);

    setLiveAngles({ ...angles });
    setLiveSide(side);

    const now = videoRef.current ? videoRef.current.currentTime * 1000 : Date.now();
    analyzerRef.current.addFrame(angles, now);
    setFrameCount(analyzerRef.current.frameCount);

    drawSkeleton(landmarks, angles, side);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlay = () => {
    setIsAnalyzing(true);
    setShowResults(false);
    setStatus("analyzing");
    startCyclingPoseDetection(videoRef.current, handlePoseResults, analyzerRef);
  };

  const handleComputeResults = () => {
    const res = analyzerRef.current.computeResults();
    setResults(res);
    setShowResults(true);
    setStatus("done");
    
    // Run validation if debug mode is enabled
    if (debugMode && res) {
      const validation = validateMeasurementConsistency(res);
      printValidationReport(validation);
    }
  };

  const toggleDebugMode = () => {
    const newDebugMode = !debugMode;
    setDebugMode(newDebugMode);
    if (newDebugMode) {
      enableDebugMode();
    } else {
      disableDebugMode();
    }
  };

  const handleReset = () => {
    analyzerRef.current.reset();
    setFrameCount(0);
    setResults(null);
    setShowResults(false);
    setLiveAngles({});
    setStatus("idle");
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleBikeTypeChange = (newBikeType) => {
    setBikeType(newBikeType);
    analyzerRef.current.setBikeType(newBikeType);
    // Re-compute results if we already have data
    if (frameCount > 0 && showResults) {
      const res = analyzerRef.current.computeResults();
      setResults(res);
    }
  };

  // ── Skeleton canvas drawing (white background, separate from video) ──
  const drawSkeleton = useCallback((landmarks, angles, side) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const w = videoDimensions.w;
    const h = videoDimensions.h;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);

    // Skeleton connections
    ctx.lineWidth = 3;
    ctx.strokeStyle = "rgba(80, 140, 220, 0.5)";
    for (const [a, b] of SKELETON_CONNECTIONS) {
      const pA = landmarks[a];
      const pB = landmarks[b];
      if (!pA || !pB) continue;
      ctx.beginPath();
      ctx.moveTo(pA.x * w, pA.y * h);
      ctx.lineTo(pB.x * w, pB.y * h);
      ctx.stroke();
    }

    // Joint dots
    for (let i = 11; i <= 32; i++) {
      const p = landmarks[i];
      if (!p) continue;
      ctx.beginPath();
      ctx.arc(p.x * w, p.y * h, 5, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(80, 140, 220, 0.8)";
      ctx.fill();
    }

    // Angle arcs + labels
    for (const angleDef of CYCLING_ANGLES) {
      const value = angles[angleDef.id];
      if (value === undefined || isNaN(value)) continue;

      const angleStatus = getAngleStatus(angleDef, value);
      const positions = getAngleLandmarkPositions(angleDef, landmarks, side, w, h);
      if (!positions || positions.some(p => !p)) continue;

      if (angleDef.isCustomCompute) {
        drawTorsoAngle(ctx, positions, value, angleStatus.color, angleDef.label);
      } else {
        drawAngleArc(ctx, positions[0], positions[1], positions[2], value, angleStatus.color, angleDef.label);
      }
    }
  }, [videoDimensions]);

  // ── Auto-load test video ──────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const testParam = params.get("test");
    if (testParam && videoRef.current) {
      setStatus("loading");
      videoRef.current.src = "/test-cycling.mp4";
      videoRef.current.onloadeddata = () => {
        videoRef.current.pause();
        setVideoDimensions({ w: videoRef.current.videoWidth, h: videoRef.current.videoHeight });
        setVideoLoaded(true);
        setStatus("ready");
        if (testParam === "auto") {
          setTimeout(() => { if (videoRef.current) videoRef.current.play(); }, 500);
        }
      };
    }
  }, []);

  // ── Sidebar ───────────────────────────────────────────────────
  const openSidebar = () => {
    const btn = document.querySelector(".sidebar-mobile-toggle");
    if (btn) btn.click();
  };

  useEffect(() => {
    const floatingBtn = document.querySelector(".sidebar-mobile-toggle");
    if (floatingBtn) {
      floatingBtn.style.opacity = "0";
      floatingBtn.style.pointerEvents = "none";
    }
    return () => {
      if (floatingBtn) {
        floatingBtn.style.opacity = "";
        floatingBtn.style.pointerEvents = "";
      }
    };
  }, []);

  // ── Render ────────────────────────────────────────────────────
  const hasAngles = Object.keys(liveAngles).length > 0;

  return (
    <div className="exercise-page-root" data-testid="cycling-page">
      <div data-testid="page-status" style={{ display: "none" }}>{status}</div>

      {/* Header */}
      <header className="exercise-header">
        <div className="flex items-center gap-2">
          <button onClick={openSidebar} className="exercise-menu-btn lg:hidden" aria-label="Open menu">
            <MenuIcon sx={{ fontSize: 20 }} />
          </button>
          <nav className="flex items-center gap-1 min-w-0" aria-label="Breadcrumb">
            <Link to="/catalog" className="text-sm font-medium text-text-muted hover:text-text-emphasis transition-colors whitespace-nowrap" style={{ textDecoration: "none" }}>
              Exercises
            </Link>
            <ChevronRightIcon sx={{ fontSize: 16, color: "var(--color-text-muted)", flexShrink: 0 }} />
            <h1 className="text-lg font-semibold text-text-emphasis truncate">Cycling Posture Analysis</h1>
          </nav>
        </div>
      </header>

      {/* Main — scrollable single-column when video loaded */}
      <div style={{ padding: "1rem", overflowY: "auto", maxHeight: "calc(100vh - 56px)" }}>

        {/* ── Upload zone ── */}
        {!videoLoaded && (
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            <div
              data-testid="upload-zone"
              className="flex flex-col items-center justify-center border-2 border-dashed rounded-2xl p-12 cursor-pointer transition-colors"
              style={{ borderColor: "var(--color-border)", minHeight: "320px", flex: "1 1 400px", background: "var(--color-surface)" }}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => document.getElementById("cycling-video-input").click()}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-4 opacity-40">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
              </svg>
              <p className="text-lg font-medium opacity-60 mb-1">Upload cycling video</p>
              <p className="text-sm opacity-40">Side view for best results. Drag & drop or click to browse.</p>
              <input id="cycling-video-input" type="file" accept="video/*" className="hidden" onChange={handleVideoUpload} />
            </div>
            <div className="card bg-base-100 shadow-sm" style={{ flex: "1 1 280px", maxWidth: "380px" }}>
              <div className="card-body p-4">
                <h3 className="card-title text-base">How it works</h3>
                <ol className="list-decimal list-inside text-sm space-y-1 opacity-70">
                  <li>Upload a side-view video of cycling</li>
                  <li>Click "Start Analysis"</li>
                  <li>AI measures 6 key angles in real time</li>
                  <li>Get injury prevention feedback</li>
                </ol>
                <p className="text-xs opacity-50 mt-2">
                  3–5 seconds of steady pedaling is enough. Non-cycling frames are automatically filtered out.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Responsive layout: 2-column (desktop) / 1-column (mobile) ── */}
        <div className="cycling-layout" style={{ display: videoLoaded ? "flex" : "none", gap: "1.5rem", alignItems: "flex-start" }}>

          {/* ── LEFT COLUMN: video + skeleton stacked ── */}
          <div className="cycling-video-column">
            {/* Bike Type Selector */}
            <div className="flex items-center gap-3 mb-3 p-3 rounded-lg" style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}>
              <span className="text-sm font-medium opacity-70">Bike Type:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBikeTypeChange(BIKE_TYPES.RECREATIONAL)}
                  className={`btn btn-sm ${bikeType === BIKE_TYPES.RECREATIONAL ? 'btn-primary' : 'btn-outline'}`}
                  disabled={isAnalyzing}
                >
                  Recreational/Endurance
                </button>
                <button
                  onClick={() => handleBikeTypeChange(BIKE_TYPES.RACE)}
                  className={`btn btn-sm ${bikeType === BIKE_TYPES.RACE ? 'btn-primary' : 'btn-outline'}`}
                  disabled={isAnalyzing}
                >
                  Race/Performance
                </button>
              </div>
            </div>

            {/* Video with Start Analysis overlay */}
            <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
              <video
                ref={videoRef}
                data-testid="video-player"
                className="cycling-video"
                style={{ maxWidth: "100%", maxHeight: "40vh", display: "block", borderRadius: "12px" }}
                controls
                onPlay={handlePlay}
              />
              {videoLoaded && !isAnalyzing && !showResults && frameCount === 0 && (
                <button
                  data-testid="btn-start-analysis"
                  onClick={() => { if (videoRef.current) videoRef.current.play(); }}
                  style={{
                    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", cursor: "pointer",
                    background: "rgba(0,0,0,0.5)", borderRadius: "12px", border: "none",
                  }}
                >
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%", background: "rgba(113,51,234,0.9)",
                    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10,
                  }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                  </div>
                  <span style={{ color: "white", fontSize: "1.1rem", fontWeight: 600 }}>Start Analysis</span>
                  <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", marginTop: 4 }}>AI will detect pose & measure angles</span>
                </button>
              )}
              {isAnalyzing && (
                <div
                  data-testid="analyzing-badge"
                  style={{
                    position: "absolute", top: 10, left: 10, display: "flex", alignItems: "center",
                    gap: 6, padding: "4px 12px", borderRadius: 999, background: "rgba(0,0,0,0.6)",
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", animation: "pulse 1.5s infinite" }} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "white" }}>Analyzing</span>
                  <span style={{ fontSize: 12, color: "#d1d5db" }}>{frameCount} frames</span>
                </div>
              )}
            </div>

            {/* Skeleton canvas (white bg, always visible once video loaded) */}
            <div style={{
              marginTop: "0.75rem",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 12,
              overflow: "hidden",
              background: "#fff",
              aspectRatio: `${videoDimensions.w} / ${videoDimensions.h}`,
              maxHeight: "40vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}>
              <canvas
                ref={canvasRef}
                data-testid="overlay-canvas"
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                }}
              />
              {!hasAngles && (
                <div style={{
                  position: "absolute", inset: 0,
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  pointerEvents: "none",
                }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ opacity: 0.15, marginBottom: 8 }}>
                    <circle cx="12" cy="5" r="2.5" />
                    <path d="M12 7.5v5M12 12.5l-3 5M12 12.5l3 5M12 9.5l-4-1.5M12 9.5l4-1.5" />
                  </svg>
                  <span style={{ fontSize: "0.85rem", opacity: 0.3, fontWeight: 500 }}>Waiting for analysis</span>
                  <span style={{ fontSize: "0.75rem", opacity: 0.2, marginTop: 2 }}>Pose skeleton will appear here</span>
                </div>
              )}
            </div>

            {/* Controls row */}
            <div className="flex gap-2 mt-2 flex-wrap">
              <button className="btn btn-sm btn-outline" onClick={() => { setVideoLoaded(false); handleReset(); }}>
                Upload New Video
              </button>
              {frameCount > 0 && !showResults && (
                <button data-testid="btn-compute" className="btn btn-sm btn-primary" onClick={handleComputeResults}>
                  Compute Results
                </button>
              )}
              {frameCount > 0 && (
                <button className="btn btn-sm btn-ghost" onClick={handleReset}>Reset Data</button>
              )}
              <button 
                className={`btn btn-sm ${debugMode ? 'btn-warning' : 'btn-ghost'}`}
                onClick={toggleDebugMode}
                title="Toggle detailed console logging for testing"
              >
                {debugMode ? '🔍 Debug ON' : '🔍 Debug'}
              </button>
            </div>
          </div>

          {/* ── RIGHT COLUMN: analysis panel ── */}
          <div className="cycling-analysis-column">
            {/* Ready prompt (before analysis) */}
            {!isAnalyzing && frameCount === 0 && !showResults && (
              <div className="card shadow-sm" style={{ background: "rgba(113,51,234,0.06)" }}>
                <div className="card-body p-4">
                  <h3 className="card-title text-base">Video loaded</h3>
                  <p className="text-sm opacity-70">
                    Click <strong>Start Analysis</strong> on the video to begin. The AI will process each frame and measure 6 key cycling angles in real time.
                  </p>
                  <div className="mt-3">
                    <p className="text-xs opacity-50 mb-2">Angles measured:</p>
                    <div className="grid grid-cols-2 gap-1">
                      {getCyclingAnglesForBikeType(bikeType).map((a) => (
                        <div key={a.id} className="text-xs opacity-60 flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#7133EA" }} />
                          {a.label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Live angles during analysis */}
            {(isAnalyzing || (frameCount > 0 && !showResults)) && (
              <div className="card bg-base-100 shadow-sm" data-testid="live-angles">
                <div className="card-body p-4">
                  <h3 className="card-title text-base">
                    Live Angles
                    {liveSide && <span className="badge badge-sm badge-ghost ml-2">{liveSide} side</span>}
                    <span className="badge badge-sm badge-outline ml-2">{getBikeTypeDisplayName(bikeType)}</span>
                  </h3>
                  <div className="space-y-2 mt-2">
                    {getCyclingAnglesForBikeType(bikeType).map((angleDef) => {
                      const value = liveAngles[angleDef.id];
                      const valid = value !== undefined && !isNaN(value);
                      const st = valid ? getAngleStatus(angleDef, value) : null;
                      return (
                        <div key={angleDef.id} data-testid={`live-angle-${angleDef.id}`}
                          className="flex items-center justify-between rounded-lg px-3 py-2"
                          style={{ background: "var(--color-surface-alt, rgba(128,128,128,0.06))" }}
                        >
                          <div>
                            <div className="text-sm font-medium">{angleDef.label}</div>
                            <div className="text-xs opacity-40">{angleDef.description}</div>
                          </div>
                          <div className="text-right">
                            <span className="text-xl font-bold tabular-nums" style={{ color: st?.color || "inherit" }}>
                              {valid ? `${(Math.round(value * 10) / 10).toFixed(1)}°` : "—"}
                            </span>
                            <div className="text-xs opacity-40">{angleDef.optimalMin}–{angleDef.optimalMax}°</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-xs opacity-50 mt-3">{frameCount} frames collected</div>
                </div>
              </div>
            )}

            {/* Results */}
            {showResults && results && (
              <div data-testid="results-panel">
                <ResultsPanel results={results} />
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Results Panel ──────────────────────────────────────────────

function ResultsPanel({ results }) {
  if (!results.stable) {
    return (
      <div className="card bg-base-100 shadow-sm" data-testid="results-incomplete">
        <div className="card-body p-4">
          <h3 className="card-title text-base">Analysis Incomplete</h3>
          <p className="text-sm opacity-70">{results.reason}</p>
          <div className="mt-3 space-y-1">
            <p className="text-xs opacity-50">
              <strong>Frame Statistics:</strong>
            </p>
            <p className="text-xs opacity-50">
              • Total frames processed: {results.totalFramesProcessed || 0}
            </p>
            <p className="text-xs opacity-50">
              • Frames with pose detected: {results.framesWithPose || 0}
            </p>
            <p className="text-xs opacity-50">
              • Frames rejected (no pose): {results.framesRejected || 0}
            </p>
            <p className="text-xs opacity-50">
              • Stable cycling frames: {results.stableFrameCount || 0}
            </p>
            {results.accuracy && (
              <p className="text-xs opacity-50">
                • Accuracy: <span style={{ textTransform: 'capitalize' }}>{results.accuracy}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const issues = [];
  const good = [];

  const cyclingAngles = getCyclingAnglesForBikeType(results.bikeType);
  for (const angleDef of cyclingAngles) {
    const r = results.angles[angleDef.id];
    if (!r || !r.valid) continue;
    if (r.status.status === "optimal") {
      good.push({ angleDef, result: r });
    } else {
      issues.push({ angleDef, result: r });
    }
    if (r.rangeWarning) {
      issues.push({
        angleDef,
        result: { ...r, status: { status: "warning", color: "#eab308", message: r.rangeWarning.message } },
        isRange: true,
      });
    }
  }

  // Determine accuracy display
  const accuracyConfig = {
    high: { color: '#22c55e', label: 'High', icon: '✓', description: 'Voldoende data voor betrouwbare meting' },
    good: { color: '#84cc16', label: 'Good', icon: '✓', description: 'Goede hoeveelheid data' },
    moderate: { color: '#eab308', label: 'Moderate', icon: '!', description: 'Minimale data, metingen kunnen minder nauwkeurig zijn' },
    low: { color: '#ef4444', label: 'Low', icon: '⚠', description: 'Onvoldoende data voor betrouwbare meting' },
    insufficient: { color: '#9ca3af', label: 'Insufficient', icon: '×', description: 'Te weinig data' },
  };
  const accuracyInfo = accuracyConfig[results.accuracy] || accuracyConfig.moderate;

  return (
    <div className="space-y-3" data-testid="results-content">
      {/* Summary */}
      <div className="card shadow-sm" data-testid="results-summary"
        style={{ background: issues.length === 0 ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.08)" }}>
        <div className="card-body p-4">
          <h3 className="card-title text-base">
            {issues.length === 0 ? "All angles look good!" : `${issues.length} issue${issues.length > 1 ? "s" : ""} detected`}
          </h3>
          <div className="flex gap-4 text-xs opacity-60">
            <span>{results.stableFrameCount} stable frames ({results.stablePercentage}%)</span>
            {results.cadenceRPM && <span>~{results.cadenceRPM} RPM</span>}
          </div>
        </div>
      </div>

      {/* Frame Quality & Accuracy */}
      <div className="card bg-base-100 shadow-sm" data-testid="results-quality">
        <div className="card-body p-4">
          <h4 className="font-semibold text-sm mb-3">Data Kwaliteit</h4>
          
          {/* Accuracy indicator */}
          <div className="rounded-lg p-3 mb-3" style={{ background: `${accuracyInfo.color}15`, border: `1px solid ${accuracyInfo.color}40` }}>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '1.2rem' }}>{accuracyInfo.icon}</span>
              <div>
                <div className="text-sm font-semibold" style={{ color: accuracyInfo.color }}>
                  Nauwkeurigheid: {accuracyInfo.label}
                </div>
                <div className="text-xs opacity-70">{accuracyInfo.description}</div>
              </div>
            </div>
          </div>

          {/* Frame statistics */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="opacity-60">Totaal verwerkte frames</span>
              <span className="font-medium">{results.totalFramesProcessed}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="opacity-60">Frames met pose gedetecteerd</span>
              <span className="font-medium text-green-600">{results.framesWithPose}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="opacity-60">Frames niet gebruikt (geen pose)</span>
              <span className="font-medium text-red-600">{results.framesRejected}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="opacity-60">Stabiele cyclische frames</span>
              <span className="font-medium text-blue-600">{results.stableFrameCount}</span>
            </div>
            
            {/* Pedal Cycle Detection */}
            {results.pedalCycles && results.pedalCycles.detected && (
              <>
                <div className="h-px bg-gray-200 my-2"></div>
                <div className="text-xs font-semibold opacity-70 mt-2 mb-1">Pedal Cycle Detection</div>
                <div className="flex justify-between text-xs">
                  <span className="opacity-60">Valid pedal cycles detected</span>
                  <span className="font-medium text-purple-600">{results.pedalCycles.totalCycles}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="opacity-60">BDC frames used (knee)</span>
                  <span className="font-medium">{results.pedalCycles.validBDCFrames}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="opacity-60">TDC frames used (hip)</span>
                  <span className="font-medium">{results.pedalCycles.validTDCFrames}</span>
                </div>
                <div className="text-xs opacity-50 mt-1 italic">
                  ℹ️ Using exact BDC/TDC frames only (1 per cycle)
                </div>
              </>
            )}
            
            <div className="h-px bg-gray-200 my-2"></div>
            <div className="flex justify-between text-xs">
              <span className="opacity-60">Gebruik van gedetecteerde frames</span>
              <span className="font-medium">{Math.round((results.stableFrameCount / results.framesWithPose) * 100)}%</span>
            </div>
            
            {/* Bike Type */}
            {results.bikeType && (
              <>
                <div className="h-px bg-gray-200 my-2"></div>
                <div className="flex justify-between text-xs">
                  <span className="opacity-60">Bike Type</span>
                  <span className="font-medium">{getBikeTypeDisplayName(results.bikeType)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Issues */}
      {issues.length > 0 && (
        <div className="card bg-base-100 shadow-sm" data-testid="results-issues">
          <div className="card-body p-4 space-y-3">
            <h4 className="font-semibold text-sm">Issues</h4>
            {issues.map(({ angleDef, result, isRange }, i) => (
              <AngleResultRow key={`issue-${i}`} angleDef={angleDef} result={result} isRange={isRange} />
            ))}
          </div>
        </div>
      )}

      {/* Good */}
      {good.length > 0 && (
        <div className="card bg-base-100 shadow-sm" data-testid="results-good">
          <div className="card-body p-4 space-y-3">
            <h4 className="font-semibold text-sm">Looking Good</h4>
            {good.map(({ angleDef, result }, i) => (
              <AngleResultRow key={`good-${i}`} angleDef={angleDef} result={result} />
            ))}
          </div>
        </div>
      )}

      {/* Bike Adjustment Tips */}
      <AdjustmentTipsPanel results={results} />

      {/* Detail table */}
      <div className="card bg-base-100 shadow-sm" data-testid="results-table">
        <div className="card-body p-4">
          <h4 className="font-semibold text-sm mb-2">Detailed Measurements</h4>
          <div className="overflow-x-auto">
            <table className="table table-xs">
              <thead>
                <tr>
                  <th>Angle</th>
                  <th className="text-right">Evaluated</th>
                  <th className="text-right">Measurement</th>
                  <th className="text-right">Range</th>
                  <th className="text-right">Optimal</th>
                </tr>
              </thead>
              <tbody>
                {getCyclingAnglesForBikeType(results.bikeType).map((angleDef) => {
                  const r = results.angles[angleDef.id];
                  if (!r || !r.valid) return null;
                  return (
                    <tr key={angleDef.id}>
                      <td className="font-medium">{angleDef.label}</td>
                      <td className="text-right tabular-nums">
                        <span style={{ color: r.status.color, fontWeight: 600 }}>{r.evaluatedValue}°</span>
                      </td>
                      <td className="text-right text-xs opacity-60">
                        {r.measurementPosition || 'N/A'}
                        {r.outliersRemoved > 0 && (
                          <div className="text-xs text-orange-600">
                            ({r.outliersRemoved} outliers removed)
                          </div>
                        )}
                      </td>
                      <td className="text-right tabular-nums text-xs opacity-60">{r.min}° - {r.max}°</td>
                      <td className="text-right tabular-nums text-xs opacity-60">{angleDef.optimalMin}–{angleDef.optimalMax}°</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Adjustment Tips Panel ───────────────────────────────────────

function AdjustmentTipsPanel({ results }) {
  const bikeFitTips = generateBikeFitTips(results);

  if (!bikeFitTips.hasRecommendations) {
    return null;
  }

  const { grouped, disclaimer } = bikeFitTips;

  // Define the priority order for display
  const adjustmentOrder = [
    { key: 'saddle_height', label: 'Saddle Height', icon: '↕️' },
    { key: 'saddle_fore_aft', label: 'Saddle Position', icon: '↔️' },
    { key: 'handlebar_height', label: 'Handlebar Height', icon: '↕️' },
    { key: 'handlebar_reach', label: 'Handlebar Reach', icon: '↔️' },
    { key: 'saddle_tilt', label: 'Saddle Tilt', icon: '↗️' },
    { key: 'handlebar_tilt', label: 'Handlebar Tilt', icon: '↗️' },
  ];

  const hasAdjustments = adjustmentOrder.some(({ key }) => grouped[key] && grouped[key].length > 0);

  if (!hasAdjustments) {
    return null;
  }

  return (
    <div className="card bg-base-100 shadow-sm" data-testid="adjustment-tips-panel">
      <div className="card-body p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <span style={{ fontSize: '1.1rem' }}>🔧</span>
              Bike Adjustment Tips
            </h4>
            <p className="text-xs opacity-60 mt-1">Concrete recommendations based on your angles</p>
          </div>
        </div>

        <div className="space-y-4">
          {adjustmentOrder.map(({ key, label, icon }) => {
            const recs = grouped[key];
            if (!recs || recs.length === 0) return null;

            return (
              <div key={key}>
                <div className="text-xs font-semibold opacity-70 mb-2 flex items-center gap-1.5">
                  <span>{icon}</span>
                  {label}
                </div>
                <div className="space-y-2">
                  {recs.map((rec, idx) => (
                    <AdjustmentRecommendationRow key={idx} recommendation={rec} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 p-3 rounded-lg text-xs" style={{ background: 'rgba(113,51,234,0.08)' }}>
          <div className="flex items-start gap-2">
            <span className="opacity-60" style={{ fontSize: '0.9rem' }}>ℹ️</span>
            <div className="opacity-70">
              <strong>Important:</strong> {disclaimer}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Adjustment Recommendation Row ───────────────────────────────

function AdjustmentRecommendationRow({ recommendation }) {
  const { action, estimateMm, unit, reason, detail, confidence, isGeneral } = recommendation;

  const confidenceColor = {
    high: '#22c55e',
    medium: '#eab308',
    low: '#94a3b8',
  };

  return (
    <div className="rounded-lg p-3" style={{ background: "var(--color-surface-alt, rgba(128,128,128,0.06))" }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-medium text-sm">
            {action}
            {estimateMm && unit && (
              <span className="ml-2 font-bold" style={{ color: '#7133EA' }}>
                ~{estimateMm}{unit}
              </span>
            )}
          </div>
          {reason && (
            <div className="text-xs opacity-60 mt-1">{reason}</div>
          )}
        </div>
        {confidence && !isGeneral && (
          <div 
            className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
            style={{ 
              background: `${confidenceColor[confidence]}20`,
              color: confidenceColor[confidence],
              fontWeight: 500,
            }}
          >
            {confidence}
          </div>
        )}
      </div>
      {detail && (
        <div className="mt-2 text-xs opacity-60 italic">{detail}</div>
      )}
    </div>
  );
}

// ── Angle result row ────────────────────────────────────────────

function AngleResultRow({ angleDef, result, isRange }) {
  const dot = { optimal: "bg-green-500", warning: "bg-yellow-500", risk: "bg-red-500" };

  return (
    <div className="rounded-lg p-3" style={{ background: "var(--color-surface-alt, rgba(128,128,128,0.06))" }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${dot[result.status.status]} flex-shrink-0 mt-0.5`} />
          <div>
            <div className="font-medium text-sm">{angleDef.label}{isRange ? " (ROM)" : ""}</div>
            <div className="text-xs opacity-50">{angleDef.description}</div>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-lg font-bold tabular-nums" style={{ color: result.status.color }}>
            {isRange ? `${(Math.round(result.rangeWarning?.range * 10) / 10).toFixed(1)}°` : `${(Math.round(result.evaluatedValue * 10) / 10).toFixed(1)}°`}
          </div>
          <div className="text-xs opacity-40">
            {isRange ? `max ${angleDef.maxRange}°` : `optimal ${angleDef.optimalMin}–${angleDef.optimalMax}°`}
          </div>
        </div>
      </div>
      {result.status.status !== "optimal" && (
        <div className="mt-2 text-xs rounded-md px-2 py-1.5" style={{ background: `${result.status.color}15`, color: result.status.color }}>
          {result.status.message}
          {angleDef.fix && <span className="opacity-70"> → {angleDef.fix}</span>}
        </div>
      )}
    </div>
  );
}

// ── Canvas drawing helpers ──────────────────────────────────────

function drawAngleArc(ctx, pA, pB, pC, angleDeg, color, label) {
  const angleA = Math.atan2(pA.y - pB.y, pA.x - pB.x);
  const angleC = Math.atan2(pC.y - pB.y, pC.x - pB.x);

  const limbLen = Math.min(
    Math.hypot(pA.x - pB.x, pA.y - pB.y),
    Math.hypot(pC.x - pB.x, pC.y - pB.y)
  );
  const arcRadius = Math.max(20, Math.min(40, limbLen * 0.3));

  // Limbs
  ctx.beginPath();
  ctx.moveTo(pA.x, pA.y);
  ctx.lineTo(pB.x, pB.y);
  ctx.lineTo(pC.x, pC.y);
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.stroke();

  // Arc
  let start = angleA, end = angleC;
  let diff = end - start;
  while (diff > Math.PI) { diff -= 2 * Math.PI; end -= 2 * Math.PI; }
  while (diff < -Math.PI) { diff += 2 * Math.PI; end += 2 * Math.PI; }

  ctx.beginPath();
  ctx.arc(pB.x, pB.y, arcRadius, start, end, diff < 0);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Arc fill
  ctx.beginPath();
  ctx.moveTo(pB.x, pB.y);
  ctx.arc(pB.x, pB.y, arcRadius, start, end, diff < 0);
  ctx.closePath();
  ctx.fillStyle = color + "20";
  ctx.fill();

  // Label
  const mid = start + diff / 2;
  const lx = pB.x + Math.cos(mid) * (arcRadius + 18);
  const ly = pB.y + Math.sin(mid) * (arcRadius + 18);
  const text = `${label} ${(Math.round(angleDeg * 10) / 10).toFixed(1)}°`;
  ctx.font = "bold 14px system-ui, sans-serif";
  const m = ctx.measureText(text);

  fillRoundedRect(ctx, lx - 4, ly - 13, m.width + 8, 18, 4, "rgba(0,0,0,0.6)");
  ctx.fillStyle = color;
  ctx.fillText(text, lx, ly);
}

function drawTorsoAngle(ctx, positions, angleDeg, color, label) {
  const [shoulder, hip] = positions;

  ctx.beginPath();
  ctx.moveTo(shoulder.x, shoulder.y);
  ctx.lineTo(hip.x, hip.y);
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.stroke();

  // Horizontal reference
  ctx.beginPath();
  ctx.moveTo(hip.x - 40, hip.y);
  ctx.lineTo(hip.x + 40, hip.y);
  ctx.strokeStyle = "rgba(0,0,0,0.15)";
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.stroke();
  ctx.setLineDash([]);

  const torsoRad = Math.atan2(hip.y - shoulder.y, shoulder.x - hip.x);
  ctx.beginPath();
  ctx.arc(hip.x, hip.y, 28, 0, -torsoRad, true);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.stroke();

  const mx = (shoulder.x + hip.x) / 2;
  const my = (shoulder.y + hip.y) / 2;
  const text = `${label} ${(Math.round(angleDeg * 10) / 10).toFixed(1)}°`;
  ctx.font = "bold 14px system-ui, sans-serif";
  const m = ctx.measureText(text);

  fillRoundedRect(ctx, mx - 4, my - 22, m.width + 8, 18, 4, "rgba(0,0,0,0.6)");
  ctx.fillStyle = color;
  ctx.fillText(text, mx, my - 8);
}

function fillRoundedRect(ctx, x, y, w, h, r, fill) {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}
